import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ArrowRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Account = {
  id: number
  name: string
  website: string
  industry: string
  size: '' | 'SOLO' | 'SMALL' | 'MEDIUM' | 'LARGE' | 'ENTERPRISE'
  country_code: string
  lifecycle_stage: 'PROSPECT' | 'CUSTOMER' | 'PARTNER' | 'LOST'
  partner_name: string | null
  owner_username: string | null
  contact_count: number
}

// Minimal create payload — full editing happens on the record page.
type CreateInput = {
  name: string
  website: string
  industry: string
  size: Account['size']
  country_code: string
  lifecycle_stage: Account['lifecycle_stage']
}

const STAGE_COLOR: Record<Account['lifecycle_stage'], string> = {
  PROSPECT: 'bg-blue-100 text-blue-800',
  CUSTOMER: 'bg-green-100 text-green-800',
  PARTNER: 'bg-purple-100 text-purple-800',
  LOST: 'bg-red-100 text-red-700',
}

const SIZE_LABEL: Record<string, string> = {
  SOLO: '1', SMALL: '2-50', MEDIUM: '51-250', LARGE: '251-1000', ENTERPRISE: '1000+',
}

const STAGE_FILTERS: ('ALL' | Account['lifecycle_stage'])[] = [
  'ALL', 'PROSPECT', 'CUSTOMER', 'PARTNER', 'LOST',
]

export default function CRMAccounts() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const { data, isLoading } = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStage, setFilterStage] = useState<'ALL' | Account['lifecycle_stage']>('ALL')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Account>('/crm/accounts/', {
      ...draft,
      annual_revenue: null,
      phone: '',
      email: '',
      address_line1: '',
      city: '',
      postal_code: '',
      partner: null,
      owner: null,
      notes: '',
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['crm', 'accounts'] })
      toast.success('Account created')
      setCreating(false)
      navigate(`/admin/crm/accounts/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/accounts/${id}/`),
    onSuccess: () => {
      toast.success('Account deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'accounts'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const accounts = data || []
  const filtered = filterStage === 'ALL' ? accounts : accounts.filter((a) => a.lifecycle_stage === filterStage)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Accounts</h2>
          <p className="text-sm text-muted-foreground">{accounts.length} total</p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New account</Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Lifecycle:</span>
        {STAGE_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStage(s)}
            className={`px-3 py-1 rounded ${filterStage === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No accounts.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Name</Th>
                <Th>Industry</Th>
                <Th>Size</Th>
                <Th>Country</Th>
                <Th>Stage</Th>
                <Th className="text-center">Contacts</Th>
                <Th>Owner</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/crm/accounts/${a.id}`)}
                >
                  <Td>
                    <div className="font-medium text-foreground">{a.name}</div>
                    {a.website && (
                      <a
                        href={a.website} target="_blank" rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-blue-600 hover:underline"
                      >{a.website}</a>
                    )}
                  </Td>
                  <Td className="text-xs">{a.industry || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{a.size ? SIZE_LABEL[a.size] : <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="font-mono text-xs">{a.country_code || <span className="text-muted-foreground">—</span>}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STAGE_COLOR[a.lifecycle_stage]}`}>
                      {a.lifecycle_stage}
                    </span>
                    {a.partner_name && (
                      <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                        <ArrowRight className="h-3 w-3" /> {a.partner_name}
                      </div>
                    )}
                  </Td>
                  <Td className="text-center text-xs">{a.contact_count}</Td>
                  <Td className="text-xs">{a.owner_username || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/crm/accounts/${a.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `account ${a.name}`,
                            () => deleteMut.mutate(a.id),
                          )}
                          className="text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Rest filled in on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New account"
        description="Create the record with the essentials. Address, contact info, and notes are filled in on the account's record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="acc-name">Account name *</FieldLabel>
            <Input id="acc-name" autoFocus required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="acc-website">Website</FieldLabel>
              <Input id="acc-website" type="url" value={draft.website}
                onChange={(ev) => setDraft({ ...draft, website: ev.target.value })}
                placeholder="https://…" />
            </Field>
            <Field>
              <FieldLabel htmlFor="acc-industry">Industry</FieldLabel>
              <Input id="acc-industry" value={draft.industry}
                onChange={(ev) => setDraft({ ...draft, industry: ev.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="acc-size">Size</FieldLabel>
              <Select value={draft.size || '__none__'}
                onValueChange={(v) => setDraft({ ...draft, size: (v === '__none__' ? '' : v) as Account['size'] })}>
                <SelectTrigger id="acc-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  <SelectItem value="SOLO">SOLO — 1 employee</SelectItem>
                  <SelectItem value="SMALL">SMALL — 2-50</SelectItem>
                  <SelectItem value="MEDIUM">MEDIUM — 51-250</SelectItem>
                  <SelectItem value="LARGE">LARGE — 251-1,000</SelectItem>
                  <SelectItem value="ENTERPRISE">ENTERPRISE — 1,000+</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="acc-country">Country (ISO 2)</FieldLabel>
              <Input id="acc-country" maxLength={2} value={draft.country_code}
                onChange={(ev) => setDraft({ ...draft, country_code: ev.target.value.toUpperCase() })}
                className="font-mono" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="acc-stage">Lifecycle stage</FieldLabel>
            <Select value={draft.lifecycle_stage}
              onValueChange={(v) => setDraft({ ...draft, lifecycle_stage: v as Account['lifecycle_stage'] })}>
              <SelectTrigger id="acc-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PROSPECT">Prospect</SelectItem>
                <SelectItem value="CUSTOMER">Customer</SelectItem>
                <SelectItem value="PARTNER">Partner</SelectItem>
                <SelectItem value="LOST">Lost</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    name: '', website: '', industry: '', size: '',
    country_code: '', lifecycle_stage: 'PROSPECT',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
